/**
 * Shared time primitives for process-velocity calculations.
 *
 * "Time in state" is the core operational primitive throughout the
 * Gabhan intelligence layer. Every risk function derives urgency from
 * how long an entity has been in a given state — not from an external
 * deadline field. These helpers centralize that date arithmetic.
 *
 * derive, never store — all calculations use live Date.now()
 */

const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

/** Milliseconds elapsed since a given date/string */
export const msElapsed = (date) => Date.now() - new Date(date).getTime();

/** Hours elapsed since a given date (float) */
export const hoursElapsed = (date) => msElapsed(date) / MS_PER_HOUR;

/** Days elapsed since a given date (float) */
export const daysElapsed = (date) => msElapsed(date) / MS_PER_DAY;

/** Integer days elapsed, floored */
export const daysElapsedFloor = (date) => Math.floor(daysElapsed(date));

/**
 * Days between two dates (signed, float).
 * Positive = `to` is after `from`.
 */
export const daysBetween = (from, to) =>
  (new Date(to).getTime() - new Date(from).getTime()) / MS_PER_DAY;

/**
 * Format elapsed hours as a compact age string.
 *   < 24h → "3h"
 *   ≥ 24h → "2d"
 */
export const formatElapsedHours = (hours) =>
  hours < 24 ? `${Math.floor(hours)}h` : `${Math.floor(hours / 24)}d`;

/**
 * Format elapsed hours as a past-tense age string.
 *   < 24h → "3h ago"
 *   ≥ 24h → "2d ago"
 */
export const formatAge = (hours) => {
  const h = Math.floor(hours);
  return h >= 24 ? `${Math.floor(h / 24)}d ago` : `${h}h ago`;
};
